<script setup lang="ts">
import { computed, ref } from 'vue';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import OverlayPanel from 'primevue/overlaypanel';
import { useLocalStorage, useStringFunctions } from '@apparatix/composables';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import { Column } from '@apparatix/types/index';
import { button as greenButtonTheme } from '@apparatix/theme/Green';

interface ColumnDisplayProps {
  tableColumns: Column[];
  visibleColumns: string[];
  defaultTableColumns: Column[];
  defaultVisibleColumns: string[];
  storagePrefix: string;
}

const props = defineProps<ColumnDisplayProps>();

const emits = defineEmits<{
  (e: 'update:visibleColumns', visible: string[]): void;
  (e: 'update:tableColumns', columns: Column[]): void;
}>();

const searchQuery = ref('');
const overlayRef = ref();

const { isEmptyString } = useStringFunctions();
const { setItem, getItem } = useLocalStorage();

const filteredColumns = computed(() => {
  if (isEmptyString(searchQuery.value, true)) {
    return props.tableColumns;
  }

  return props.tableColumns.filter(column => column.header.toLowerCase().includes(searchQuery.value.trim().toLowerCase()));
});

const toggle = (e: MouseEvent) => overlayRef.value.toggle(e);

const reset = () => {
  emits('update:tableColumns', props.defaultTableColumns);
  emits('update:visibleColumns', props.defaultVisibleColumns);
};

const save = (e: MouseEvent) => {
  setItem({ key: `${props.storagePrefix}:visible-columns`, value: props.visibleColumns });
  setItem({ key: `${props.storagePrefix}:column-order`, value: props.tableColumns });
  toggle(e);
};

// Drag-and-drop logic
const dragIndex = ref<number | null>(null);
const isHovered = ref();

const onDragStart = (index: number, event: DragEvent) => {
  dragIndex.value = index;
  const target = event.currentTarget as HTMLElement;
  target.classList.add('dragging');
};

const onDragEnd = (event: DragEvent) => {
  const target = event.currentTarget as HTMLElement;
  target.classList.remove('dragging');
};

const onDrop = (dropIndex: number) => {
  if (dragIndex.value === null || dragIndex.value === dropIndex) return;

  const reorderedColumns = [...props.tableColumns];
  const draggedItem = reorderedColumns.splice(dragIndex.value, 1)[0];
  reorderedColumns.splice(dropIndex, 0, draggedItem);

  emits('update:tableColumns', reorderedColumns);
  dragIndex.value = null;
};

const getVisibleColumns = (): string[] => getItem(`${props.storagePrefix}:visible-columns`) ?? props.defaultVisibleColumns;
const getTableColumns = (): Column[] => getItem(`${props.storagePrefix}:column-order`) ?? props.defaultTableColumns;

const disableSave = () => JSON.stringify([...props.visibleColumns].sort()) === JSON.stringify([...getVisibleColumns()].sort()) && JSON.stringify([...props.tableColumns]) === JSON.stringify([...getTableColumns()]);
const disableReset = () => JSON.stringify([...props.visibleColumns].sort()) === JSON.stringify([...props.defaultVisibleColumns].sort()) && JSON.stringify([...props.tableColumns]) === JSON.stringify([...props.defaultTableColumns]);

const localVisibleColumns = computed({
  get() {
    return props.visibleColumns;
  },
  set(value) {
    emits('update:visibleColumns', value);
  },
});

</script>
<template>
  <Button
    v-tooltip.left="'Configure List'"
    size="small"
    outlined
    severity="secondary"
    class="flex items-center w-full"
    @click="toggle"
  >
    <i class="pi pi-cog" />
    <span class="font-bold whitespace-nowrap">Configure List</span>
  </Button>
  <OverlayPanel
    ref="overlayRef"
    unstyled
    :pt="{
      root: {
        style: {
          boxShadow: 'var(--popover-box-shadow)',
        },
        class: 'rounded-lg bg-transparent'
      },
      content: {
        class: 'rounded-lg bg-transparent'
      }
    }"
  >
    <div class="flex flex-col max-h-[50vh] lg:max-h-[66vh] lg:min-h-[33vh] max-h-screen rounded-lg border border-[var(--popover-border-color)] overflow-hidden bg-[var(--white)]">
      <div class="flex-none flex flex-col gap-2 p-3 bg-[var(--light-neutral-background)]">
        <div class="w-full">
          <IconField icon-position="left">
            <InputIcon class="pi pi-search" />
            <InputText v-model="searchQuery" placeholder="Search Columns" />
          </IconField>
        </div>
      </div>
      <div class="flex-none flex flex-col gap-1 py-2 px-3 border-t border-[var(--popover-border-color)]" />
      <div class="flex-1 overflow-y-auto flex flex-col gap-2 px-3">
        <div
          v-for="(column, index) in filteredColumns"
          :key="column.field"
          class="flex gap-2 items-center draggable-item cursor-move select-none"
          draggable="true"
          :class="{ 'hovered': isHovered === index }"
          @dragstart="onDragStart(index, $event)"
          @dragover.prevent
          @dragenter.prevent
          @dragend="onDragEnd($event)"
          @drop="onDrop(index)"
          @mouseover="isHovered = index"
          @mouseleave="isHovered = null"
        >
          <Checkbox
            v-model="localVisibleColumns"
            :value="column.field"
            :input-id="column.field"
            name="category"
          />
          <label :for="column.field">{{ column.header }}</label>
          <i
            v-show="isHovered === index"
            class="pi pi-bars icon ml-auto px-2 transition-opacity duration-[0.3s] ease-[ease]"
          />
        </div>
      </div>
      <div class="flex-none flex flex-row justify-between items-center p-3 bg-[var(--light-neutral-background)] w-full">
        <Button
          v-tooltip.left="'Reset'"
          size="small"
          outlined
          severity="secondary"
          :disabled="disableReset()"
          class="flex items-center"
          @click="reset"
        >
          <span class="font-bold"> Reset </span>
        </Button>
        <Button
          v-tooltip.left="'Save'"
          size="small"
          severity="primary"
          :disabled="disableSave()"
          class="flex items-center"
          :dt="greenButtonTheme"
          @click="save"
        >
          <span class="font-bold"> Save </span>
        </Button>
      </div>
    </div>
  </OverlayPanel>
</template>

<style scoped>
    .p-button-secondary {
      background-color: lightgray;
      background: var(--white);
      border: 1px solid var(--black-16);
      color: var(--black-80);
    }

    .p-badge-secondary {
      background-color: lightgray;
      background: var(--white);
      border: 1px solid var(--black-16);
      color: var(--black-80);
    }

    .draggable-item.dragging {
      opacity: 0.5;
      background-color: lightgrey;
    }

    .draggable-item.hovered {
      background-color: lightgrey;
    }
  </style>
